import { CircularProgress, Stack } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { fetchAvailabilitiesAction } from 'redux/slices/iCalendarV4/availabilitySlice';

import MyAvailabilityCard from 'app/calendarV4/availability/MyAvailabilityCard';
import MyAvailabilityForm from 'app/calendarV4/availability/myAvailabilityForm/MyAvailabilityForm';
import ActionButton from 'common/components/ActionButton';
import CalendarType from 'common/enums/calendarTypes';
import useFlexBoxStyles from 'common/flexBox';
import LayoutForm from 'common/layouts/LayoutForm';
import { useTranslation } from 'react-i18next';
import { closeNestedDialogAction } from 'redux/slices/nestedDialogSlice';

import 'suneditor/dist/css/suneditor.min.css';

const ServiceFormCalendarAvailability = ({ calendar, handleSubmit }) => {
    const dispatch = useDispatch();
    const { classes: flexBox, cx } = useFlexBoxStyles();
    const { t } = useTranslation();
    const [pending, setPending] = useState(true);
    const [availabilities, setAvailabilities] = useState([]);
    const [openAdd, setOpenAdd] = useState(false);
    const [availabilitySelected, setAvailabilitySelected] = useState(null);
    const [newAvailability, setNewAvailability] = useState(null);
    useEffect(() => {
        const current = calendar.availability;
        if (!current) return;
        setAvailabilitySelected(current);
        if (!current.id) {
            setNewAvailability(current);
        }
    }, [calendar]);

    const loadData = useCallback(
        (page = 1) => {
            setPending(true);

            dispatch(
                fetchAvailabilitiesAction({
                    queryParams: {
                        calendarId: calendar.canonicalId,
                        page,
                        limit: 1000,
                    },
                    onSuccess: ({ items }) => {
                        setAvailabilities(items);
                        setPending(false);
                    },
                    options: { useState: false },
                }),
            );
        },
        [dispatch, calendar],
    );

    useEffect(() => {
        if (calendar.type === CalendarType.AGENT) {
            loadData();
        } else {
            setPending(false);
        }
    }, [calendar.type, loadData]);

    const closeNested = () => dispatch(closeNestedDialogAction());

    const handleCloseAdd = (status) => {
        if (status === 'cancel' && availabilities.length === 0) {
            closeNested();
        } else {
            setOpenAdd(false);
        }
    };

    const handleDelete = () => {
        setNewAvailability(null);
        setAvailabilitySelected((a) => (!a?.id ? null : a));
        if (availabilities.length === 0) {
            handleSubmit(null);
            closeNested();
        }
    };

    const handleFormSubmit = (data, cancel) => {
        if (
            calendar.type !== CalendarType.AGENT ||
            (calendar.type === CalendarType.AGENT && availabilities.length === 0)
        ) {
            handleSubmit(data);
        } else {
            setNewAvailability(data);
            setAvailabilitySelected(data);
            cancel();
        }
    };

    const handleFinalSave = () => {
        handleSubmit({ ...availabilitySelected });
    };

    const showAddButton = !openAdd && !pending && availabilities.length > 0 && !newAvailability;

    const showForm = pending || openAdd || (availabilities.length === 0 && !newAvailability);

    return (
        <LayoutForm title={t('oneToOne.availability')}>
            {showAddButton && (
                <Stack direction="row" alignItems="center" justifyContent="flex-end">
                    <ActionButton
                        color="primary"
                        autoFocus
                        fitWidth
                        disabled={pending}
                        onClick={() => setOpenAdd(true)}
                    >
                        {t('availability.createAction.add')}
                    </ActionButton>
                </Stack>
            )}

            <Stack
                direction="column"
                className={cx(flexBox.wrap, flexBox.w100, flexBox.h100, flexBox.overflowXHidden, flexBox.scrollbarNone)}
            >
                {pending && (
                    <Stack direction="row" justifyContent="center" alignItems="center" sx={{ height: 200 }}>
                        <CircularProgress size={36} />
                    </Stack>
                )}

                {(showForm || calendar.type !== CalendarType.AGENT) && !pending && (
                    <MyAvailabilityForm
                        isServiceForm
                        type={calendar.type}
                        onSubmit={handleFormSubmit}
                        onClose={handleCloseAdd}
                        userGroupId={calendar.userGroupId}
                        availability={calendar.type === CalendarType.AGENT ? newAvailability : availabilitySelected}
                    />
                )}
                {!pending && !showForm && calendar.type === CalendarType.AGENT && (
                    <Stack direction="column" spacing={1}>
                        {[...(newAvailability ? [newAvailability] : []), ...availabilities]?.map((item) => (
                            <MyAvailabilityCard
                                key={item.id}
                                availability={item}
                                calendar={calendar}
                                selected={availabilitySelected}
                                onSelect={setAvailabilitySelected}
                                onDelete={!item.id ? handleDelete : null}
                                onEdit={!item.id ? () => setOpenAdd(true) : null}
                            />
                        ))}
                    </Stack>
                )}
            </Stack>

            {!showForm && calendar.type === CalendarType.AGENT && (
                <Stack
                    direction="row"
                    justifyContent="flex-end"
                    alignItems="center"
                    spacing={1}
                    className={flexBox.paddingTop2}
                >
                    <ActionButton onClick={closeNested} color="default" fitWidth disabled={pending}>
                        {t('multimedia.multimediaForm.cancel')}
                    </ActionButton>

                    <ActionButton
                        color="primary"
                        autoFocus
                        fitWidth
                        disabled={pending || !availabilitySelected}
                        onClick={handleFinalSave}
                    >
                        {t('event.eventFormSettings.save')}
                    </ActionButton>
                </Stack>
            )}
        </LayoutForm>
    );
};

export default ServiceFormCalendarAvailability;
